import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { EUR_COUNTRIES, EUR_CURRENCY, EURO_TIME_ZONES } from '../data/market.config';
import { BILLING_CURRENCY } from '../data/pricing.config';

/** What `PricingService` sends to `GET /marketing/pricing`. Optional; absent means the platform default. */
export interface RegionQuery {
  readonly country?: string;
}

const LOOKUP_URL = 'https://api.country.is/';
const LOOKUP_TIMEOUT_MS = 2500;

interface CountryIsResponse {
  readonly country?: string;
}

/**
 * The region the browser's own language settings imply, e.g. `fr-BE` to BE and
 * a bare `fr` to FR, which is what `maximize()` fills in. Costs no request and
 * nothing can block it.
 */
function localeCountry(): string | null {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];

  for (const tag of tags) {
    try {
      const region = new Intl.Locale(tag).maximize().region;
      if (region) return region.toUpperCase();
    } catch {
      // A malformed tag is not worth failing over; try the next one.
    }
  }

  return null;
}

/**
 * Where the browser says it is, with no request. The time zone wins over the
 * language: someone in Paris with an en-GB browser is in France, and someone in
 * New York with a French one is not. A zone outside the list means the default market.
 */
function browserCountry(): string | null {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return zone ? (EURO_TIME_ZONES[zone] ?? null) : localeCountry();
}

/**
 * Owns the visitor's pricing region.
 *
 * Detection calls `country.is` straight from the browser, so avyna.io never
 * receives an IP; a missing, slow or malformed answer is ordinary, not an
 * error, since `query()` just has nothing to send until it resolves.
 *
 * **The lookup is blocked for a good share of this audience.** `api.country.is`
 * is an IP-geolocation endpoint, which puts it on uBlock Origin's privacy
 * lists, behind Brave's shields and Firefox's strict tracking protection, and
 * on plenty of corporate DNS filters — and the people evaluating software for
 * an Intune estate are exactly the people who run those. A blocked lookup is
 * indistinguishable here from a slow one, and both used to end in the platform
 * default market, which for a European reader means prices in the wrong
 * currency.
 *
 * So a failed lookup falls back to what the browser itself reports rather than
 * to nothing. It cannot be blocked, and the prices it chooses are labelled
 * indicative either way. The same signal sets `currency` from the first render,
 * so a European reader sees euros before any request answers, and even if none
 * ever does.
 */
@Injectable({ providedIn: 'root' })
export class RegionService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly _detectedCountry = signal<string | null>(null);
  private readonly _settled = signal(false);

  /**
   * True once detection has finished — answered, failed or timed out.
   * Callers wait on this so the first price fetch carries the region, rather
   * than fetching the default market and correcting it a moment later.
   */
  readonly settled = this._settled.asReadonly();

  readonly query = computed<RegionQuery>(() => {
    const country = this._detectedCountry();
    return country ? { country } : {};
  });

  /** The currency to show before, or without, live prices. */
  readonly currency = computed(() => {
    const country = this._detectedCountry() ?? this.browserHint;
    return country && EUR_COUNTRIES.has(country) ? EUR_CURRENCY : BILLING_CURRENCY;
  });

  private readonly browserHint = this.isBrowser ? browserCountry() : null;
  private started = false;

  /** Starts background detection. Never awaited; a no-op after the first call. */
  detect(): void {
    // A prerender has no visitor: its pages keep the fallback amounts until the browser looks up.
    if (this.started || !this.isBrowser) return;
    this.started = true;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);

    fetch(LOOKUP_URL, { signal: controller.signal })
      .then((response) => (response.ok ? (response.json() as Promise<CountryIsResponse>) : null))
      .then((body) => this.applyDetected(body?.country))
      .catch(() => undefined)
      .finally(() => {
        clearTimeout(timer);
        // Blocked, refused, timed out or answered with nothing usable: all of
        // them land here with no country set, and all of them are better served
        // by the browser's own signal than by the platform default.
        if (!this._detectedCountry()) this.applyDetected(this.browserHint ?? undefined);
        this._settled.set(true);
      });
  }

  private applyDetected(country: string | undefined): void {
    if (!country) return;
    this._detectedCountry.set(country.toUpperCase());
  }
}
